import React, { useMemo } from 'react';

import { Grid, Text } from '@mantine/core';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import { ClassMeta } from 'hooks/class/useClassMeta';
import { Link } from 'react-router-dom';
import { KiloClassAthleteRetrieve } from 'types';

const ClassMainSection = ({
  classData,
  classMeta,
}: {
  classData: KiloClassAthleteRetrieve;
  classMeta: ClassMeta;
}): JSX.Element => {
  const { dateTimeFormat } = useLocalization();
  const { inClassCount } = classMeta;

  const locationText = useMemo(() => {
    if (classData.location && classData.location.url) {
      return (
        <Text size='lg' color='gray.7' weight={700}>
          <Link target='_blank' rel='noopener noreferrer' to={classData.location.url}>
            {classData.location.name}
          </Link>
        </Text>
      );
    }
    if (classData.location) {
      return (
        <Text size='lg' color='gray.7' weight={700}>
          {classData.location.name}
        </Text>
      );
    }
    return '-';
  }, [classData]);

  const fullStartDate = new Date(`${classData.startDate}T${classData.startTime}`);

  const reservationWindowOpen = classData.reservationAndCancellationSettings
    ?.reservationWindowOpenIntervalCount
    ? dayjs(fullStartDate)
        .subtract(
          classData.reservationAndCancellationSettings?.reservationWindowOpenIntervalCount,
          classData.reservationAndCancellationSettings?.reservationWindowOpenInterval || 'minutes',
        )
        .format(dateTimeFormat)
    : '';

  const reservationWindowClose = classData.reservationAndCancellationSettings
    ?.reservationWindowCloseIntervalCount
    ? dayjs(fullStartDate)
        .subtract(
          classData.reservationAndCancellationSettings?.reservationWindowCloseIntervalCount,
          classData.reservationAndCancellationSettings?.reservationWindowCloseInterval || 'minutes',
        )
        .format(dateTimeFormat)
    : dayjs(fullStartDate).format(dateTimeFormat);

  const cancellationBeforeDate = `Before ${dayjs(fullStartDate)
    .subtract(
      classData.reservationAndCancellationSettings?.cancellationIntervalCount || 0,
      classData.reservationAndCancellationSettings?.cancellationInterval || 'minutes',
    )
    .format(dateTimeFormat)}`;

  return (
    <Grid columns={6} align='top' gutter='xs'>
      <Grid.Col span={2}>
        <Text align='right' color='dimmed' fs='italic'>
          Location
        </Text>
      </Grid.Col>
      <Grid.Col span={4}>{locationText}</Grid.Col>

      <Grid.Col span={2}>
        <Text align='right' color='dimmed' fs='italic'>
          Class Size
        </Text>
      </Grid.Col>
      <Grid.Col span={4}>
        <Text size='lg' color='gray.7' weight={700}>
          {inClassCount} / {classData.capacity}
        </Text>
      </Grid.Col>

      <Grid.Col span={2}>
        <Text align='right' color='dimmed' fs='italic'>
          Reservability
        </Text>
      </Grid.Col>
      <Grid.Col span={4}>
        <Text size='lg' color='gray.7' weight={700}>
          {reservationWindowOpen !== ''
            ? `${reservationWindowOpen} to ${reservationWindowClose}`
            : `Before ${fullStartDate}`}
        </Text>
      </Grid.Col>

      <Grid.Col span={2}>
        <Text align='right' color='dimmed' fs='italic'>
          Cancellation
        </Text>
      </Grid.Col>
      <Grid.Col span={4}>
        <Text size='lg' color='gray.7' weight={700}>
          {cancellationBeforeDate}
        </Text>
      </Grid.Col>
    </Grid>
  );
};

export default ClassMainSection;
